import { AHSeparator, Box, ListItem, useBoxStyle } from "arkh";
import React, { useCallback, useContext, useMemo } from "react";
import { SectionList } from "react-native";
import { usePreloadedQuery, useQueryLoader, graphql } from "react-relay";

import {
  TaskListItemIcon,
  TaskListItemTitle,
} from "../../../../../../components/TaskListItem";
import { WidgetTaskCollectionContext } from "../WidgetTaskCollectionContext";
import { RoutineSectionVersionSectionHeaderContent } from "./RoutineSectionVersionSectionHeaderContent";
import { WTCSStackScreenProps } from "./WTCSStack";
import { TaskSelectListEmpty } from "../../../../../../components/TaskSelectListEmpty";
import { WTCSSelectTasksQuery } from "./__generated__/WTCSSelectTasksQuery.graphql";

export const WTCS_SELECT_TASKS_QUERY = graphql`
  query WTCSSelectTasksQuery($routineId: ID!) {
    routine(id: $routineId) {
      id
      title
      activeVersion {
        sections {
          id
          ...RoutineSectionVersionSectionHeaderContent_RoutineSectionVersion
          section {
            id
            title
          }
          tasks {
            id
            title
            __typename
            ...TaskListItemIcon_Task
            ...TaskListItemTitle_Task
          }
        }
      }
    }
  }
`;

export function WTCSSelectTasks(
  props: WTCSStackScreenProps<"WTCSSelectTasks">
) {
  const context = useContext(WidgetTaskCollectionContext);

  const [queryRef] = useQueryLoader(
    WTCS_SELECT_TASKS_QUERY,
    props.route.params.queryRef
  );

  if (!queryRef) {
    throw new Error("WATaskCollectionSettingsTasks: queryRef is null");
  }
  const data = usePreloadedQuery<WTCSSelectTasksQuery>(
    WTCS_SELECT_TASKS_QUERY,
    queryRef
  );

  const contentStyle = useBoxStyle({
    space: "medium",
    spaceTop: "medium",
  });

  const sections = useMemo(() => {
    return (
      data.routine?.activeVersion.sections.flatMap((section) =>
        section.tasks.length > 0
          ? [
              {
                data: section.tasks.flatMap((task) => {
                  if (task.__typename === "TaskText") {
                    return [];
                  }
                  return [
                    {
                      ...task,
                      sectionId: section.section.id,
                      title: task.title,
                      sectionTitle: section.section.title ?? "",
                    },
                  ];
                }),
                section,
                id: section.id,
              },
            ]
          : []
      ) ?? []
    );
  }, [data.routine?.activeVersion.sections]);

  type Section = (typeof sections)[number];
  type Item = Section["data"][number];
  const onPressItem = useCallback(
    (item: Item, selected: boolean) => {
      if (selected) {
        context.setSelectedTasks((tasks) =>
          tasks.filter(
            (t) => !(t.id === item.id && t.sectionId === item.sectionId)
          )
        );
      } else if (context.selectedTasks.length < 8) {
        context.setSelectedTasks((tasks) => [
          ...tasks,
          {
            id: item.id,
            sectionId: item.sectionId,
            ordinal: tasks.length,
            title: item.title,
            sectionTitle: item.sectionTitle,
            routineTitle: data.routine?.title ?? "",
          },
        ]);
      }
    },
    [context, data.routine?.title]
  );

  const renderItem = useCallback(
    ({
      item,
      index,
      section,
    }: {
      item: Item;
      index: number;
      section: Section;
    }) => {
      const selected =
        context.selectedTasks.findIndex(
          (task) => task.id === item.id && task.sectionId === item.sectionId
        ) !== -1;
      return (
        <ListItem
          border="border-primary"
          onPress={() => {
            onPressItem(item, selected);
          }}
          isFirst={index === 0}
          isLast={index === section.data.length - 1}
          left={<TaskListItemIcon task={item} />}
          right={
            <ListItem.SelectIndicator
              selected={selected}
              disabled={!selected && context.selectedTasks.length === 8}
            />
          }
        >
          <TaskListItemTitle task={item} />
        </ListItem>
      );
    },
    [context.selectedTasks, onPressItem]
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: Section }) => (
      <RoutineSectionVersionSectionHeaderContent section={section.section} />
    ),
    []
  );

  const renderSectionFooter = useCallback(() => <Box spaceTop="large" />, []);

  return (
    <SectionList
      contentContainerStyle={contentStyle}
      sections={sections}
      ListEmptyComponent={
        <TaskSelectListEmpty routineTitle={data.routine?.title ?? ""} />
      }
      ItemSeparatorComponent={AHSeparator}
      renderSectionHeader={renderSectionHeader}
      renderSectionFooter={renderSectionFooter}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
    />
  );
}

function keyExtractor(item: { id: string; sectionId: string }) {
  return `${item.sectionId}${item.id}`;
}
